import {
  Grid,
  Paper,
  Box,
  Stack,
  Skeleton,
  Divider,
  TableRow,
  TableCell,
} from "@mui/material";

export const SimulationCardSkeleton = () => {
  return (
    <Grid item xs={12} sm={6} md={4}>
      <Paper
        elevation={3}
        sx={{
          borderRadius: "12px",
          overflow: "hidden",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          border: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        {/* Content */}
        <Box sx={{ p: 3, flexGrow: 1 }}>
          {/* Header */}
          <Stack direction="row" justifyContent="space-between" mb={2}>
            <Skeleton variant="text" width="50%" height={30} />
            <Skeleton variant="rounded" width={70} height={28} />
          </Stack>

          {/* Simulation Code */}
          <Stack spacing={1.5} mb={2}>
            <Skeleton variant="text" width="60%" />

            <Stack direction="row" spacing={1} alignItems="center">
              <Skeleton variant="circular" width={20} height={20} />
              <Skeleton variant="text" width="70%" />
            </Stack>
          </Stack>

          <Divider sx={{ my: 2 }} />

          {/* Groups */}
          <Box mb={2}>
            <Skeleton variant="text" width={120} />
            <Stack direction="row" spacing={1} mt={1}>
              {[1, 2, 3].map((_, i) => (
                <Skeleton key={i} variant="circular" width={38} height={38} />
              ))}
            </Stack>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Progress */}
          <Box>
            <Stack direction="row" justifyContent="space-between" mb={1}>
              <Skeleton variant="text" width={80} />
              <Skeleton variant="text" width={120} />
            </Stack>

            <Skeleton
              variant="rounded"
              height={8}
              sx={{ borderRadius: 4, mb: 2 }}
            />

            {/* Time */}
            <Stack direction="row" spacing={1} alignItems="center">
              <Skeleton variant="circular" width={16} height={16} />
              <Skeleton variant="text" width={100} />
            </Stack>
          </Box>
        </Box>

        {/* Button */}
        <Box
          sx={{
            p: 2,
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Skeleton variant="rounded" height={36} sx={{ borderRadius: 2 }} />
        </Box>
      </Paper>
    </Grid>
  );
};

export const GroupCardSkeleton = () => {
  return (
    <Grid item xs={12} sm={6} md={4}>
      <Paper
        variant="outlined"
        elevation={3}
        sx={{
          borderRadius: 3,
          p: 2.5,
          boxShadow: 3,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Stack spacing={2} sx={{ flexGrow: 1 }}>
          {/* Row 1: Title + Error Icon */}
          <Stack direction="row" justifyContent="space-between">
            <Skeleton variant="text" width="50%" height={28} />
            <Skeleton variant="circular" width={22} height={22} />
          </Stack>

          {/* Row 2: Code + Category */}
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Skeleton variant="text" width={90} />
              <Skeleton variant="rounded" width={80} height={24} />
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <Skeleton variant="text" width={80} />
              <Skeleton variant="rounded" width={120} height={24} />
            </Stack>
          </Stack>

          <Divider />

          {/* Row 3: Team Details */}
          <Box>
            <Skeleton variant="text" width={120} />

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mt={1}
            >
              <Skeleton variant="text" width={140} />

              <Stack direction="row" spacing={1}>
                {[1, 2, 3, 4].map((_, i) => (
                  <Skeleton key={i} variant="circular" width={36} height={36} />
                ))}
              </Stack>
            </Stack>
          </Box>

          <Divider />

          {/* Row 4: Progress */}
          <Box>
            <Skeleton variant="text" width={100} />
            <Skeleton
              variant="rounded"
              height={8}
              sx={{ mt: 1, borderRadius: 4 }}
            />
          </Box>

          {/* Bottom Buttons */}
          <Stack direction="row" spacing={1} sx={{ mt: "auto", pt: 1 }}>
            <Skeleton
              variant="rounded"
              height={36}
              sx={{ flexGrow: 1, borderRadius: 2 }}
            />
            <Skeleton
              variant="rounded"
              width={40}
              height={36}
              sx={{ borderRadius: 1 }}
            />
          </Stack>
        </Stack>
      </Paper>
    </Grid>
  );
};

export const PastSimulationSkeleton = () => {
  return (
    <Grid item xs={12} sm={6} md={4}>
      <Box
        sx={{
          borderRadius: { xs: "5px", sm: "7px", md: "10px" },
          overflow: "hidden",
          boxShadow: 1,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: { md: "3%", sm: "2%", xs: "2%" },
            bgcolor: "grey.300",
          }}
        >
          <Skeleton variant="text" width="60%" height={28} />
        </Box>

        {/* Content */}
        <Box
          sx={{
            p: { md: "5%", sm: "3%", xs: "3%" },
            bgcolor: "background.paper",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {/* Dates */}
          <Skeleton variant="text" width="80%" />
          <Skeleton variant="text" width="75%" />

          {/* Code */}
          <Skeleton variant="text" width="60%" />

          {/* Bottom Section */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              mt: 1,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Skeleton variant="circular" width={32} height={32} />
              <Skeleton variant="circular" width={28} height={28} />
            </Stack>
          </Box>
        </Box>
      </Box>
    </Grid>
  );
};

export const ModuleItemSkeleton = () => {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        mb: 2,
        bgcolor: "background.paper",
      }}
    >
      {/* Title + Status */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Skeleton variant="text" width="40%" height={24} />

        <Stack direction="row" spacing={1} alignItems="center">
          <Skeleton variant="text" width={30} />
          <Skeleton variant="rounded" width={80} height={24} />
        </Stack>
      </Box>

      {/* Description */}
      <Skeleton variant="text" width="90%" />
      <Skeleton variant="text" width="70%" sx={{ mb: 2 }} />

      {/* Stepper (fake progress blocks) */}
      <Stack direction="row" spacing={1} alignItems="center">
        {[1, 2, 3, 4].map((_, i) => (
          <Skeleton key={i} variant="circular" width={20} height={20} />
        ))}
        <Skeleton variant="rounded" height={6} sx={{ flexGrow: 1 }} />
      </Stack>
    </Box>
  );
};

export const MemberItemSkeleton = () => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        p: 2,
        borderRadius: 2,
        mb: 1.5,
        bgcolor: "background.paper",
      }}
    >
      {/* Avatar */}
      <Skeleton variant="circular" width={48} height={48} sx={{ mr: 2 }} />

      {/* Content */}
      <Box sx={{ flexGrow: 1 }}>
        {/* Name + Chip */}
        <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
          <Skeleton variant="text" width="40%" height={24} />
          <Skeleton variant="rounded" width={80} height={20} />
        </Stack>

        {/* Email */}
        <Skeleton variant="text" width="60%" />
      </Box>

      {/* Action Icon */}
      <Skeleton variant="circular" width={32} height={32} />
    </Box>
  );
};

export const TableRowSkeleton = () => {
  return (
    <TableRow>
      {/* Row Number */}
      <TableCell sx={{ width: 70 }}>
        <Skeleton variant="text" width={20} />
      </TableCell>

      {/* Name */}
      <TableCell sx={{ maxWidth: 150 }}>
        <Skeleton variant="text" width="80%" />
      </TableCell>

      {/* Institution */}
      <TableCell sx={{ maxWidth: 150 }}>
        <Skeleton variant="text" width="70%" />
      </TableCell>

      {/* Email */}
      <TableCell sx={{ maxWidth: 300 }}>
        <Skeleton variant="text" width="90%" />
      </TableCell>

      {/* Mobile */}
      <TableCell sx={{ maxWidth: 250 }}>
        <Skeleton variant="text" width="60%" />
      </TableCell>

      {/* Members Count */}
      <TableCell sx={{ maxWidth: 100 }}>
        <Skeleton variant="text" width={30} />
      </TableCell>

      {/* Button */}
      <TableCell align="center">
        <Skeleton variant="rounded" width={60} height={30} />
      </TableCell>
    </TableRow>
  );
};

export const CategoryTableRowSkeleton = () => {
  return (
    <TableRow hover>
      {/* Index */}
      <TableCell>
        <Skeleton variant="text" width={20} />
      </TableCell>

      {/* Name */}
      <TableCell>
        <Skeleton variant="text" width="70%" />
      </TableCell>
    </TableRow>
  );
};

export const QueryTableRowSkeleton = () => {
  return (
    <TableRow hover>
      {/* Index */}
      <TableCell>
        <Skeleton variant="text" width={20} />
      </TableCell>

      {/* Name */}
      <TableCell>
        <Skeleton variant="text" width="70%" />
      </TableCell>

      {/* Date */}
      <TableCell>
        <Skeleton variant="text" width="60%" />
      </TableCell>

      {/* Institution */}
      <TableCell>
        <Skeleton variant="text" width="75%" />
      </TableCell>

      {/* Query Type */}
      <TableCell>
        <Skeleton variant="text" width="65%" />
      </TableCell>

      {/* Status Chip */}
      <TableCell>
        <Skeleton
          variant="rounded"
          width={80}
          height={24}
          sx={{ borderRadius: 2 }}
        />
      </TableCell>

      {/* Email Link */}
      <TableCell>
        <Skeleton variant="text" width="85%" />
      </TableCell>

      {/* Action */}
      <TableCell align="center">
        <Skeleton variant="circular" width={30} height={30} />
      </TableCell>
    </TableRow>
  );
};
